import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../utils/Api";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import StepNumber from "../components/StepNumber";
import PageIntro from "../components/PageIntro";
import heroPrints from "../assets/heroes/hero-prints.webp";
import ErrorModal from "../components/ErrorModal";
import { motion, AnimatePresence } from "framer-motion";
import { Info } from "lucide-react";
import CtaBand from "../components/CtaBand";
import ctaShop from "../assets/heroes/hero-shop.webp";
import { PAGES } from "../seoContent";
import { whatsappLink, WHATSAPP_MESSAGES } from "../utils/whatsapp";

export default function Printing() {
  const { apiFetch } = useApi();
  const [prints, setPrints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState(() => {
    const saved = localStorage.getItem("kinews_print_form");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { name: "", email: "", phone: "" };
      }
    }
    return { name: "", email: "", phone: "" };
  });

  useEffect(() => {
    localStorage.setItem("kinews_print_form", JSON.stringify(form));
  }, [form]);

  const [submitting, setSubmitting] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    message: "",
  });

  const [selection, setSelection] = useState({
    category: "",
    category_id: "",
    subLabel: "",
    group_label: "",
    qty: "",
    sided: "single",
    extras: [],
  });

  const groupData = useCallback((data) => {
    const categories = {};
    data.forEach((p) => {
      const catId = p.category_id || 0;
      if (!categories[catId]) {
        categories[catId] = { 
          id: catId, 
          name: p.category, 
          metric_name: p.metric_name || "Weight (GSM)",
          subGroups: {} 
        };
      }
      // If no size is provided, use the GSM as the primary variation label
      const subLabel = p.size && p.size.trim() !== "" ? p.size : p.group_label;
      if (!categories[catId].subGroups[subLabel]) {
        categories[catId].subGroups[subLabel] = {
          label: subLabel,
          category: p.category,
          category_id: catId,
          metric_name: p.metric_name || "Weight (GSM)",
          size: p.size,
          groups: {},
          allExtras: p.extras || [],
        };
      }
      const target = categories[catId].subGroups[subLabel];
      if (!target.groups[p.group_label]) target.groups[p.group_label] = [];
      target.groups[p.group_label].push(p);
    });
    return Object.values(categories).sort(
      (a, b) => Number(a.id) - Number(b.id),
    );
  }, []);

  const loadPrints = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch("get_prints");
      if (res?.success) {
        const rawData = Array.isArray(res.data) ? res.data : [];
        setPrints(rawData);
        const grouped = groupData(rawData);
        if (grouped.length > 0) {
          const firstCat = grouped[0];
          const firstSub = Object.values(firstCat.subGroups)[0];
          const firstGroup = Object.keys(firstSub.groups)[0];
          setSelection({
            category: firstCat.name,
            category_id: firstCat.id,
            subLabel: firstSub.label,
            group_label: firstGroup,
            qty: firstSub.groups[firstGroup][0].qty,
            sided: "single",
            extras: [],
          });
        }
      } else {
        setError(res?.message || "Failed to load services.");
      }
    } catch (err) {
      setError("Connection error.");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, groupData]);

  useEffect(() => {
    loadPrints();
  }, [loadPrints]);

  const grouped = groupData(prints);
  const selectedCategory = grouped.find(
    (c) => Number(c.id) === Number(selection.category_id),
  );
  const selectedSub = selectedCategory
    ? Object.values(selectedCategory.subGroups).find(
        (s) => s.label === selection.subLabel,
      )
    : null;

  const handleCategoryChange = (cat) => {
    const firstSub = Object.values(cat.subGroups)[0];
    const firstGroup = Object.keys(firstSub.groups)[0];
    setSelection({
      category: cat.name,
      category_id: cat.id,
      subLabel: firstSub.label,
      group_label: firstGroup,
      qty: firstSub.groups[firstGroup][0].qty,
      sided: "single",
      extras: [],
    });
  };

  const handleSubChange = (subLabel) => {
    const sub = Object.values(selectedCategory.subGroups).find(
      (s) => s.label === subLabel,
    );
    const firstGroup = Object.keys(sub.groups)[0];
    setSelection((prev) => ({
      ...prev,
      subLabel: sub.label,
      group_label: firstGroup,
      qty: sub.groups[firstGroup][0].qty,
      extras: [],
    }));
  };

  const toggleExtra = (extra) => {
    setSelection((prev) => {
      const exists = prev.extras.find((e) => e.name === extra.name);
      if (exists)
        return {
          ...prev,
          extras: prev.extras.filter((e) => e.name !== extra.name),
        };
      return { ...prev, extras: [...prev.extras, extra] };
    });
  };

  const handleCheckout = async () => {
    if (!form.name || !form.email || !form.phone) {
      setErrorModal({
        open: true,
        message: "Please fill in your details.",
      });
      return;
    }

    try {
      setSubmitting(true);
      const res = await apiFetch("create_print_checkout_session", {
        category_id: selection.category_id,
        subLabel: selection.subLabel,
        group_label: selection.group_label,
        qty: selection.qty,
        sided: selection.sided,
        extras: selection.extras,
        fullName: form.name,
        email: form.email,
        phone: form.phone
      });

      if (res?.success && res?.checkoutUrl) {
        window.location.href = res.checkoutUrl;
      } else {
        setErrorModal({
          open: true,
          message: res?.message || "Failed to start checkout session.",
        });
      }
    } catch (err) {
      console.error(err);
      setErrorModal({
        open: true,
        message: "An error occurred. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currentBundle = selectedSub?.groups[selection.group_label]?.find(
    (b) => Number(b.qty) === Number(selection.qty),
  );
  const basePrice =
    selection.sided === "single"
      ? currentBundle?.price_single || 0
      : currentBundle?.price_double || 0;
  const extrasTotal = selection.extras.reduce(
    (sum, e) => sum + Number(e.price),
    0,
  );
  const totalPrice = basePrice + extrasTotal;

  let stepNo = 0;
  const nextStep = () => {
    stepNo += 1;
    return stepNo;
  };

  const stepHeader = (n, title, subtitle) => (
    <div className="mb-6 flex items-center gap-4">
      <StepNumber n={n} />
      <div>
        <h2 className="text-2xl font-semibold">{title}</h2>
        {subtitle && <p className="text-sm text-black/50">{subtitle}</p>}
      </div>
    </div>
  );

  const cardClass =
    "relative isolate overflow-hidden rounded-3xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] md:p-8";

  const chipClass = (active) =>
    [
      "rounded-2xl border px-5 py-3 text-sm font-semibold transition",
      active
        ? "border-pink-500 bg-pink-100 text-pink-700"
        : "border-black/10 bg-white text-black/80 hover:border-pink-300 hover:bg-pink-50",
    ].join(" ");

  const inputClass =
    "w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-base text-black placeholder:text-black/40 outline-none transition focus:border-pink-500";

  const summaryRow = (label, value) => (
    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
      <span>{label}</span>
      <span className="min-w-0 text-right font-medium text-white">{value}</span>
    </div>
  );

  return (
    <>
      <SEO
        title={PAGES["/printing"].title}
        description={PAGES["/printing"].description}
      />
      <main className="min-h-screen bg-white text-black">
        <PageHero title="Prints" image={heroPrints} />

        <PageIntro>
          Order professional printing online, from leaflets, flyers and post cards to business cards, stationery and stickers. Customize your order and make payment.
        </PageIntro>

        <section className="bg-[#fff7fb]">
          <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10 lg:px-16 xl:px-10">
            {loading ? (
              <div className={`${cardClass} flex h-60 items-center justify-center`}>
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-black/10 border-t-pink-500"></div>
              </div>
            ) : error ? (
              <div className={`${cardClass} text-center`}>
                <p className="text-lg text-black/50">{error}</p>
              </div>
            ) : (
              <div className="grid gap-8 lg:grid-cols-12">
                <div className="space-y-8 lg:col-span-7">
                  {/* Product */}
                  <div className={cardClass}>
                    {stepHeader(nextStep(), "Choose a product", "What would you like printed?")}

                    <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
                      {grouped.map((cat) => {
                        const active =
                          Number(selection.category_id) === Number(cat.id);

                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleCategoryChange(cat)}
                            className={[
                              "flex items-center gap-2 rounded-2xl border p-3 text-left text-xs font-semibold leading-snug transition-all duration-300 sm:gap-3 sm:p-4 sm:text-sm",
                              active
                                ? "border-pink-500 bg-pink-100 text-pink-700"
                                : "border-black/10 bg-white text-black/75 hover:-translate-y-0.5 hover:border-pink-300",
                            ].join(" ")}
                          >
                            <span
                              className={[
                                "h-2.5 w-2.5 shrink-0 rounded-full",
                                active ? "bg-pink-500" : "bg-black/15",
                              ].join(" ")}
                            />
                            <span className="min-w-0">{cat.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Size / option */}
                  <div className={cardClass}>
                    {stepHeader(nextStep(), "Select size or option", "Choose the option for your print")}

                    <div className="flex flex-wrap gap-3">
                      {selectedCategory &&
                        Object.values(selectedCategory.subGroups).map((sub) => (
                          <button
                            key={sub.label}
                            type="button"
                            onClick={() => handleSubChange(sub.label)}
                            className={chipClass(selection.subLabel === sub.label)}
                          >
                            {sub.label}
                          </button>
                        ))}
                    </div>
                  </div>

                  {/* Type */}
                  {selectedSub &&
                    Object.keys(selectedSub.groups).length > 0 &&
                    Object.keys(selectedSub.groups)[0] !== "null" &&
                    Object.keys(selectedSub.groups)[0] !== "" && (
                      <div className={cardClass}>
                        {stepHeader(nextStep(), "Select type", "Choose the variation type for your print")}

                        <div className="flex flex-wrap gap-3">
                          {Object.keys(selectedSub.groups).map((group_label) => (
                            <button
                              key={group_label}
                              type="button"
                              onClick={() =>
                                setSelection((prev) => ({
                                  ...prev,
                                  group_label,
                                  qty: selectedSub.groups[group_label][0].qty,
                                }))
                              }
                              className={chipClass(selection.group_label === group_label)}
                            >
                              {group_label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Sides */}
                  {currentBundle?.price_double !== null && (
                    <div className={cardClass}>
                      {stepHeader(nextStep(), "Print sides", "Choose single or double sided printing")}

                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            setSelection((prev) => ({ ...prev, sided: "single" }))
                          }
                          className={chipClass(selection.sided === "single")}
                        >
                          Single sided
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setSelection((prev) => ({ ...prev, sided: "double" }))
                          }
                          className={chipClass(selection.sided === "double")}
                        >
                          Double sided
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Quantity */}
                  <div className={cardClass}>
                    {stepHeader(nextStep(), "Select quantity", "Choose the number of units to print")}

                    <div className="flex flex-wrap gap-3">
                      {selectedSub &&
                        selectedSub.groups[selection.group_label]?.map(
                          (bundle, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() =>
                                setSelection((prev) => ({
                                  ...prev,
                                  qty: bundle.qty,
                                }))
                              }
                              className={`${chipClass(
                                Number(selection.qty) === Number(bundle.qty),
                              )} min-w-[84px] text-base`}
                            >
                              {bundle.qty}
                            </button>
                          ),
                        )}
                    </div>
                  </div>

                  {/* Add-ons */}
                  <AnimatePresence mode="wait">
                    {selectedSub?.allExtras.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={cardClass}
                      >
                        {stepHeader(nextStep(), "Add-ons", "Enhance your prints with optional extras")}

                        <div className="space-y-3">
                          {selectedSub.allExtras.map((extra, idx) => {
                            const isSelected = selection.extras.find(
                              (e) => e.name === extra.name,
                            );

                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => toggleExtra(extra)}
                                className={[
                                  "flex w-full items-center gap-4 rounded-2xl border px-4 py-3.5 text-left transition",
                                  isSelected
                                    ? "border-pink-500 bg-pink-50"
                                    : "border-black/10 bg-white hover:border-pink-300",
                                ].join(" ")}
                              >
                                <span
                                  className={[
                                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition",
                                    isSelected
                                      ? "border-pink-500 bg-pink-500"
                                      : "border-black/25 bg-white",
                                  ].join(" ")}
                                >
                                  {isSelected && (
                                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                      <path
                                        d="M1 4L3.5 6.5L9 1"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                </span>
                                <span className="min-w-0 flex-1 text-sm font-semibold text-black">
                                  {extra.name}
                                </span>
                                <span
                                  className={[
                                    "text-sm font-bold tabular-nums",
                                    isSelected ? "text-pink-600" : "text-black/50",
                                  ].join(" ")}
                                >
                                  +£{Number(extra.price).toFixed(2)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Details */}
                  <div className={cardClass}>
                    {stepHeader(nextStep(), "Your details", "So we can reach you about your order")}

                    <div className="grid gap-5 sm:grid-cols-2">
                      <input
                        placeholder="Full name"
                        className={`${inputClass} sm:col-span-2`}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                      <input
                        placeholder="Email address"
                        type="email"
                        className={inputClass}
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                      <input
                        placeholder="Phone number"
                        className={inputClass}
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                    </div>

                    <div className="mt-6 flex items-start gap-4 rounded-2xl border border-pink-500/15 bg-pink-50 p-5">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-pink-500 text-white">
                        <Info size={18} strokeWidth={2.5} />
                      </span>
                      <p className="text-sm leading-relaxed text-pink-700">
                        After payment, a member of our team will contact you to
                        request your print requirements.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <aside className="lg:col-span-5">
                  <div className="rounded-3xl border border-pink-500/20 bg-[#1c1c1c] p-7 text-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] lg:sticky lg:top-28">
                    <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                      Summary
                    </p>
                    <h3 className="mt-2 text-2xl font-semibold">Print overview</h3>

                    <div className="mt-8 space-y-4 text-sm text-white/75">
                      {summaryRow("Product", selection.category)}
                      {summaryRow("Size / option", selection.subLabel)}
                      {selection.group_label &&
                        summaryRow("Type", selection.group_label)}
                      {currentBundle?.price_double !== null &&
                        summaryRow(
                          "Sides",
                          <span className="capitalize">{selection.sided}</span>,
                        )}
                      {summaryRow("Quantity", `${selection.qty} units`)}

                      {selection.extras.length > 0 && (
                        <div className="space-y-2 border-b border-white/10 pb-3">
                          <span className="block">Add-ons</span>
                          {selection.extras.map((ex, i) => (
                            <div key={i} className="flex justify-between gap-4">
                              <span className="min-w-0 flex-1 text-white">
                                {ex.name}
                              </span>
                              <span className="shrink-0 font-medium text-white">
                                +£{Number(ex.price).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mt-6 flex items-center justify-between rounded-2xl bg-white/5 px-4 py-4">
                        <span className="text-base font-semibold text-white">
                          Total
                        </span>
                        <span className="text-2xl font-semibold tabular-nums text-pink-400">
                          £{totalPrice.toFixed(2)}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={handleCheckout}
                        disabled={submitting}
                        className={[
                          "w-full rounded-2xl px-6 py-4 text-base font-semibold transition",
                          submitting
                            ? "cursor-not-allowed bg-pink-500/40 text-white/70"
                            : "bg-pink-500 text-white hover:bg-pink-400",
                        ].join(" ")}
                      >
                        {submitting ? "Redirecting..." : "Continue to Payment"}
                      </button>

                      <p className="text-center text-xs leading-relaxed text-white/50">
                        You will be taken to a secure payment page to complete
                        your order.
                      </p>
                    </div>

                    <div className="mt-6 border-t border-white/10 pt-5 text-sm text-white/70">
                      <p className="font-medium text-white">Need help choosing?</p>
                      <p className="mt-2">
                        Call{" "}
                        <a
                          href="tel:+447486160965"
                          className="text-pink-400 hover:text-pink-300"
                        >
                          +44 7486 160965
                        </a>{" "}
                        or{" "}
                        <a
                          href={whatsappLink(WHATSAPP_MESSAGES.printing)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pink-400 hover:text-pink-300"
                        >
                          message us on WhatsApp
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                </aside>
              </div>
            )}
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 lg:px-16 xl:px-10">
            <CtaBand
              heading="We Offer Custom Photo Frames, Canvas & Personalised Gifts"
              to="/shop"
              label="Shop Frames & Gifts"
              image={ctaShop}
            />
          </div>
        </section>
      </main>

      <ErrorModal
        open={errorModal.open}
        message={errorModal.message}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
      />
    </>
  );
}
